import { MCP_OAUTH_CALLBACK_PATH } from '@af/shared';
import { call } from '@/lib/api';

/** Popup ↔ app channel: the callback page posts here and closes itself. */
const CHANNEL = 'af-mcp-oauth';

export interface OAuthReturn {
  ok: boolean;
  serverId: string | null;
  message: string | null;
}

/** Redirect URI to register at the vendor for a pre-registered OAuth client. */
export const oauthRedirectUri = () => `${window.location.origin}${MCP_OAUTH_CALLBACK_PATH}`;

/**
 * Start the sign-in for an `oauth` MCP server. The popup is opened synchronously (before the API
 * call) so popup blockers allow it; without a popup the whole page navigates and comes back.
 */
export async function startMcpSignIn(serverId: string): Promise<void> {
  const popup = window.open('about:blank', CHANNEL, 'popup,width=560,height=760');
  try {
    const { authorizeUrl } = await call('startMcpOAuth', {
      params: { id: serverId },
      body: { popup: !!popup },
    });
    if (popup && !popup.closed) popup.location.href = authorizeUrl;
    else window.location.assign(authorizeUrl);
  } catch (e) {
    popup?.close();
    throw e;
  }
}

function readReturn(): { result: OAuthReturn; popup: boolean } | null {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('mcp_oauth');
  if (!status) return null;
  const result: OAuthReturn = {
    ok: status === 'ok',
    serverId: params.get('server'),
    message: params.get('message'),
  };
  const popup = params.get('popup') === '1';
  for (const k of ['mcp_oauth', 'server', 'message', 'popup']) params.delete(k);
  const qs = params.toString();
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  return { result, popup };
}

/**
 * Called at boot, before the app renders: in the sign-in popup, relay the `?mcp_oauth=` result to
 * the app window over a BroadcastChannel (the opener link is cut by COOP) and close. True when
 * this window is that popup.
 */
export function relayOAuthPopup(): boolean {
  if (!new URLSearchParams(window.location.search).has('popup')) return false;
  const r = readReturn();
  if (!r?.popup || typeof BroadcastChannel === 'undefined') return false;
  const ch = new BroadcastChannel(CHANNEL);
  ch.postMessage(r.result);
  ch.close();
  window.close();
  return true;
}

/** Read (and strip) a `?mcp_oauth=` result after a full-page (non-popup) sign-in. */
export function consumeOAuthReturn(): OAuthReturn | null {
  return readReturn()?.result ?? null;
}

/** Subscribe to sign-in results from a popup. */
export function onOAuthReturn(fn: (r: OAuthReturn) => void): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  const ch = new BroadcastChannel(CHANNEL);
  ch.onmessage = (e: MessageEvent<OAuthReturn>) => fn(e.data);
  return () => ch.close();
}
